/**
 * Jurisdiction code + region utilities (v9.2 / D-92-5).
 *
 * Single source of truth for mapping external legislative-feed
 * jurisdiction labels (country names, implementer groups, US state
 * names) to ADR-0024 jurisdiction codes, and for grouping a code into a
 * coarse region for coverage filtering (D-92-3, D-92-4).
 *
 * Shared by every legislative feed (DPA API, US state feeds, future
 * sources) so country→code mapping is not re-implemented per feed. Pure,
 * no I/O — safe to use in the deterministic applicability path.
 */
export const REGIONS = ['US', 'EU', 'APAC', 'LATAM', 'MENA', 'SSA', 'GLOBAL'];
/**
 * Multilateral / supranational implementer groups → ADR-0024 code.
 * Regional bodies map to a specific region-bearing code; cross-regional
 * bodies (G7, G20, OECD, UN) use INTL and resolve to GLOBAL (D-92-3).
 */
export const GROUP_TO_CODE = {
    'European Union': 'EU',
    'Council of Europe': 'INTL-COE',
    'African Union': 'INTL-AU',
    'ASEAN': 'INTL-ASEAN',
    'ASEAN-5': 'INTL-ASEAN',
    'Latin American Integration Association': 'INTL-LAIA',
    'G7': 'INTL-G7',
    'G20': 'INTL-G20',
    'G8': 'INTL-G8',
    'OECD members': 'INTL-OECD',
    'UN members': 'INTL-UN',
};
/** Country name → ISO 3166-1 alpha-2 (ADR-0024 national codes). */
export const COUNTRY_TO_CODE = {
    'United States of America': 'US',
    'China': 'CN',
    'United Kingdom': 'GB',
    'Republic of Korea': 'KR',
    'Brazil': 'BR',
    'Australia': 'AU',
    'Germany': 'DE',
    'Canada': 'CA',
    'Argentina': 'AR',
    'Peru': 'PE',
    'Japan': 'JP',
    'Italy': 'IT',
    'France': 'FR',
    'India': 'IN',
    'Singapore': 'SG',
    'Philippines': 'PH',
    'Vietnam': 'VN',
    'Netherlands': 'NL',
    'Chinese Taipei': 'TW',
    'Spain': 'ES',
    'Thailand': 'TH',
    'Morocco': 'MA',
    'South Africa': 'ZA',
    'Denmark': 'DK',
    'Mexico': 'MX',
    'Chile': 'CL',
    'Saudi Arabia': 'SA',
    'Russia': 'RU',
    'Poland': 'PL',
    'Hong Kong': 'HK',
    'Indonesia': 'ID',
    'Colombia': 'CO',
    'Israel': 'IL',
    'Ireland': 'IE',
    'Nigeria': 'NG',
    'New Zealand': 'NZ',
    'Norway': 'NO',
    'Turkey': 'TR',
    'Turkiye': 'TR',
    'Türkiye': 'TR',
    'Sweden': 'SE',
    'Switzerland': 'CH',
    'Belgium': 'BE',
    'Austria': 'AT',
    'Portugal': 'PT',
    'Finland': 'FI',
    'Czech Republic': 'CZ',
    'Czechia': 'CZ',
    'Romania': 'RO',
    'Hungary': 'HU',
    'Greece': 'GR',
    'Egypt': 'EG',
    'Kenya': 'KE',
    'Malaysia': 'MY',
    'Pakistan': 'PK',
    'Bangladesh': 'BD',
    'Uruguay': 'UY',
    'Costa Rica': 'CR',
    'Ecuador': 'EC',
    'Panama': 'PA',
    'Paraguay': 'PY',
    'Bolivia': 'BO',
    'Dominican Republic': 'DO',
    'Guatemala': 'GT',
    'Honduras': 'HN',
    'El Salvador': 'SV',
    'Jamaica': 'JM',
    'Trinidad and Tobago': 'TT',
    'Ukraine': 'UA',
    'Serbia': 'RS',
    'Croatia': 'HR',
    'Bulgaria': 'BG',
    'Slovakia': 'SK',
    'Slovenia': 'SI',
    'Estonia': 'EE',
    'Latvia': 'LV',
    'Lithuania': 'LT',
    'Luxembourg': 'LU',
    'Malta': 'MT',
    'Cyprus': 'CY',
    'Iceland': 'IS',
    'United Arab Emirates': 'AE',
    'Qatar': 'QA',
    'Kuwait': 'KW',
    'Bahrain': 'BH',
    'Oman': 'OM',
    'Jordan': 'JO',
    'Lebanon': 'LB',
    'Tunisia': 'TN',
    'Algeria': 'DZ',
    'Ethiopia': 'ET',
    'Ghana': 'GH',
    'Rwanda': 'RW',
    'Tanzania': 'TZ',
    'Senegal': 'SN',
    'Cambodia': 'KH',
    'Myanmar': 'MM',
    'Laos': 'LA',
    'Sri Lanka': 'LK',
    'Nepal': 'NP',
    'Uzbekistan': 'UZ',
    'Andorra': 'AD',
    'Afghanistan': 'AF',
};
/** US state name → ADR-0024 subnational code. */
export const US_STATE_TO_CODE = {
    Alabama: 'US-AL', Alaska: 'US-AK', Arizona: 'US-AZ', Arkansas: 'US-AR',
    California: 'US-CA', Colorado: 'US-CO', Connecticut: 'US-CT', Delaware: 'US-DE',
    Florida: 'US-FL', Georgia: 'US-GA', Hawaii: 'US-HI', Idaho: 'US-ID',
    Illinois: 'US-IL', Indiana: 'US-IN', Iowa: 'US-IA', Kansas: 'US-KS',
    Kentucky: 'US-KY', Louisiana: 'US-LA', Maine: 'US-ME', Maryland: 'US-MD',
    Massachusetts: 'US-MA', Michigan: 'US-MI', Minnesota: 'US-MN', Mississippi: 'US-MS',
    Missouri: 'US-MO', Montana: 'US-MT', Nebraska: 'US-NE', Nevada: 'US-NV',
    'New Hampshire': 'US-NH', 'New Jersey': 'US-NJ', 'New Mexico': 'US-NM', 'New York': 'US-NY',
    'North Carolina': 'US-NC', 'North Dakota': 'US-ND', Ohio: 'US-OH', Oklahoma: 'US-OK',
    Oregon: 'US-OR', Pennsylvania: 'US-PA', 'Rhode Island': 'US-RI', 'South Carolina': 'US-SC',
    'South Dakota': 'US-SD', Tennessee: 'US-TN', Texas: 'US-TX', Utah: 'US-UT',
    Vermont: 'US-VT', Virginia: 'US-VA', Washington: 'US-WA', 'West Virginia': 'US-WV',
    Wisconsin: 'US-WI', Wyoming: 'US-WY', 'District of Columbia': 'US-DC',
};
/**
 * Multilateral body codes → region. Regional bodies map to their region;
 * cross-regional bodies to GLOBAL (D-92-3).
 */
const MULTILATERAL_REGION = {
    'INTL-COE': 'EU', // Council of Europe — European
    'INTL-AU': 'SSA', // African Union
    'INTL-ASEAN': 'APAC', // Southeast Asia
    'INTL-LAIA': 'LATAM', // Latin American Integration Association
    'INTL-G7': 'GLOBAL',
    'INTL-G8': 'GLOBAL',
    'INTL-G20': 'GLOBAL',
    'INTL-OECD': 'GLOBAL',
    'INTL-UN': 'GLOBAL',
};
/** Country (ISO alpha-2) → region. */
const COUNTRY_REGION = {
    US: 'US',
    // EU / Europe
    EU: 'EU', GB: 'EU', DE: 'EU', FR: 'EU', IT: 'EU', ES: 'EU', NL: 'EU', SE: 'EU',
    CH: 'EU', BE: 'EU', AT: 'EU', PT: 'EU', FI: 'EU', CZ: 'EU', RO: 'EU', HU: 'EU',
    GR: 'EU', DK: 'EU', NO: 'EU', IE: 'EU', PL: 'EU', UA: 'EU', RS: 'EU', HR: 'EU',
    BG: 'EU', SK: 'EU', SI: 'EU', EE: 'EU', LV: 'EU', LT: 'EU', LU: 'EU', MT: 'EU',
    CY: 'EU', IS: 'EU', AD: 'EU', TR: 'EU',
    // APAC
    CN: 'APAC', KR: 'APAC', JP: 'APAC', AU: 'APAC', NZ: 'APAC', IN: 'APAC', SG: 'APAC',
    PH: 'APAC', VN: 'APAC', TW: 'APAC', TH: 'APAC', HK: 'APAC', ID: 'APAC', MY: 'APAC',
    PK: 'APAC', BD: 'APAC', KH: 'APAC', MM: 'APAC', LA: 'APAC', LK: 'APAC', NP: 'APAC',
    AF: 'APAC', UZ: 'APAC',
    // LATAM
    BR: 'LATAM', AR: 'LATAM', PE: 'LATAM', MX: 'LATAM', CL: 'LATAM', CO: 'LATAM',
    UY: 'LATAM', CR: 'LATAM', EC: 'LATAM', PA: 'LATAM', PY: 'LATAM', BO: 'LATAM',
    DO: 'LATAM', GT: 'LATAM', HN: 'LATAM', SV: 'LATAM', JM: 'LATAM', TT: 'LATAM',
    // MENA
    SA: 'MENA', AE: 'MENA', EG: 'MENA', MA: 'MENA', QA: 'MENA', KW: 'MENA', BH: 'MENA',
    OM: 'MENA', JO: 'MENA', LB: 'MENA', TN: 'MENA', DZ: 'MENA', IL: 'MENA',
    // SSA
    ZA: 'SSA', NG: 'SSA', KE: 'SSA', ET: 'SSA', GH: 'SSA', RW: 'SSA', TZ: 'SSA', SN: 'SSA',
    // Other
    RU: 'GLOBAL',
};
/**
 * Derive an ADR-0024 jurisdiction code from a DPA-style event record.
 * Supranational groups win; then a single implementer (US subnational
 * when `implementation_level==='subnational'`); then the first of many.
 * Returns `'UNMAPPED'` when nothing resolves.
 */
export function deriveJurisdiction(row) {
    const groups = row.implementer_groups ?? [];
    const implementers = row.implementers ?? [];
    if (groups.length > 0) {
        const code = GROUP_TO_CODE[groups[0].name];
        if (code)
            return code;
    }
    if (implementers.length === 1) {
        const name = implementers[0].name;
        if (row.implementation_level === 'subnational' && US_STATE_TO_CODE[name]) {
            return US_STATE_TO_CODE[name];
        }
        if (COUNTRY_TO_CODE[name])
            return COUNTRY_TO_CODE[name];
    }
    if (implementers.length > 0) {
        const code = COUNTRY_TO_CODE[implementers[0].name];
        if (code)
            return code;
    }
    return 'UNMAPPED';
}
/**
 * Map an ADR-0024 jurisdiction code to a coverage region (D-92-3/D-92-4).
 * Hierarchical: `US-CA` → `US`; `INTL-ASEAN` → `APAC`; unknown → GLOBAL.
 */
export function jurisdictionToRegion(code) {
    if (!code)
        return 'GLOBAL';
    const upper = code.toUpperCase();
    // Multilateral bodies (INTL-*) — explicit table.
    if (upper.startsWith('INTL')) {
        return MULTILATERAL_REGION[upper] ?? 'GLOBAL';
    }
    // US and US-subnational.
    if (upper === 'US' || upper.startsWith('US-'))
        return 'US';
    // National (or EU-subnational like EU-…): take the leading 2-letter code.
    const root = upper.split('-')[0];
    return COUNTRY_REGION[root] ?? 'GLOBAL';
}
//# sourceMappingURL=jurisdiction-codes.js.map